import {
  type ApolloError,
  useLazyQuery,
  useMutation,
  useSuspenseQuery,
} from "@apollo/client";
import {
  Alert,
  Button,
  CurrencyLabel,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EditPaymentIdentifiers,
  formInputToGqlMutationInput,
  PrimaryFullScreenSpinner,
  paymentIdentifierComponentToSetPaymentIdentifier,
  SelectItem,
  SelectSeparator,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import {
  currencySchema,
  getCountryLists,
  neverFunc,
  paymentIdentifiersEqual,
  prettyPrintEnum,
  setPaymentIdentifierSchema,
} from "@repo/utils";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import countries from "i18n-iso-countries";
import { Suspense, useEffect, useState } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import {
  beneficiaryAccountKindValues,
  beneficiaryEntityKindValues,
  currencyValues,
} from "~/gql/client/enumValues";
import type { ExistingBeneficiaryAccountFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

const formSchema = z.object({
  organisationId: z.string().nonempty({ message: "Required" }),
  businessEntityId: z.string().optional(),
  nickname: z.string(),
  accountName: z.string().nonempty({ message: "Required" }),
  currency: currencySchema,
  kind: z.enum(beneficiaryAccountKindValues),
  entityKind: z.enum(beneficiaryEntityKindValues).optional(),
  paymentIdentifiers: z.array(setPaymentIdentifierSchema),
  country: z.string().refine(countries.isValid),
  address: z
    .object({
      buildingNumber: z.string(),
      streetName: z.string(),
      city: z.string(),
      state: z.string(),
      postcode: z.string(),
      country: z.string().refine(countries.isValid),
    })
    .optional(),
});

type FormData = z.input<typeof formSchema>;

export const Route = createFileRoute("/__auth/beneficiary-accounts_/new")({
  head: () => opsHead("New Beneficiary Account"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();
  const [existingOrgAccounts, setExistingOrgAccounts] = useState<
    ExistingBeneficiaryAccountFragment[]
  >([]);
  const [pendingSubmit, setPendingSubmit] = useState<z.output<
    typeof formSchema
  > | null>(null);

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminNewBeneficiaryAccountForm {
        organisations {
          ...AdminNewBeneficiaryAccountFormOrg
        }
      }

      fragment AdminNewBeneficiaryAccountFormOrg on Organisation {
        id
        name
        businessEntities {
          id
          name
        }
      }
    `),
  );

  const [fetchOrgBeneficiaryAccounts] = useLazyQuery(
    graphql(`
      query AdminNewBeneficiaryAccountDuplicateCheck($organisationId: String!) {
        beneficiaryAccounts(
          first: 50
          filter: {organisationIds: [$organisationId], status: ACTIVE}
        ) {
          beneficiaryAccounts {
            ...ExistingBeneficiaryAccount
          }
        }
      }

      fragment ExistingBeneficiaryAccount on BeneficiaryAccount {
        id
        displayName
        currency
        paymentIdentifiers {
          ...PaymentIdentifierComponent
        }
      }
    `),
  );

  const [
    createVerifiedBeneficiaryMutation,
    { loading: createVerifiedLoading },
  ] = useMutation(
    graphql(`
      mutation AdminCreateVerifiedBeneficiaryAccount($input: CreateVerifiedBeneficiaryAccountInput!) {
        createVerifiedBeneficiaryAccount(input: $input) {
          beneficiaryAccount {
            id
          }
        }
      }
    `),
  );

  const [createPayeeBeneficiaryMutation, { loading: createPayeeLoading }] =
    useMutation(
      graphql(`
        mutation AdminCreatePayeeBeneficiaryAccount($input: CreatePayeeBeneficiaryAccountInput!) {
          createPayeeBeneficiaryAccount(input: $input) {
            beneficiaryAccount {
              id
            }
          }
        }
      `),
    );
  const loading = createVerifiedLoading ?? createPayeeLoading;

  async function runCreateMutation(parsed: z.output<typeof formSchema>) {
    const handleSuccess = (beneficiaryAccountId: string | null | undefined) => {
      toast({
        kind: "success",
        autoHide: true,
        title: "Beneficiary account created",
      });
      if (!beneficiaryAccountId) {
        return;
      }
      void navigate({
        to: "/beneficiary-accounts/$beneficiaryAccountId",
        params: { beneficiaryAccountId },
      });
    };

    const onError = (error: ApolloError) => {
      const errorMessage = error.graphQLErrors?.[0]?.message || "Server error.";
      toast({
        kind: "error",
        autoHide: false,
        title: "Error creating beneficiary account",
        description: errorMessage,
      });
    };

    switch (parsed.kind) {
      case "VERIFIED_BENEFICIARY":
        if (!parsed.businessEntityId) {
          throw new Error("verified beneficiary must have business entity");
        }
        await createVerifiedBeneficiaryMutation({
          variables: {
            input: {
              businessEntityId: parsed.businessEntityId,
              accountName: parsed.accountName,
              currency: parsed.currency,
              nickname: parsed.nickname,
              paymentIdentifiers: parsed.paymentIdentifiers.map(
                formInputToGqlMutationInput,
              ),
              country: parsed.country,
            },
          },
          onCompleted: (data) =>
            handleSuccess(
              data.createVerifiedBeneficiaryAccount?.beneficiaryAccount?.id,
            ),
          onError,
        });
        break;
      case "PAYEE":
        if (!parsed.address) {
          throw new Error("address is required for payee");
        }
        if (!parsed.entityKind) {
          throw new Error("entity kind is required for payee");
        }
        await createPayeeBeneficiaryMutation({
          variables: {
            input: {
              organisationId: parsed.organisationId,
              accountName: parsed.accountName,
              currency: parsed.currency,
              nickname: parsed.nickname,
              entityKind: parsed.entityKind,
              paymentIdentifiers: parsed.paymentIdentifiers.map(
                formInputToGqlMutationInput,
              ),
              country: parsed.country,
              address: parsed.address,
            },
          },
          onCompleted: (data) =>
            handleSuccess(
              data.createPayeeBeneficiaryAccount?.beneficiaryAccount?.id,
            ),
          onError,
        });
        break;
      default:
        neverFunc(parsed.kind);
    }
  }

  const form = useAppForm({
    defaultValues: {
      organisationId:
        new URLSearchParams(window.location.search).get("organisationId") || "",
      businessEntityId: "",
      nickname: "",
      accountName: "",
      currency: "AUD",
      kind: "VERIFIED_BENEFICIARY",
      paymentIdentifiers: [
        {
          identifierType: "au_bsb",
          paymentIdentifierAuBsb: {
            accountNumber: "",
            accountName: "",
            bsb: "",
          },
        },
      ],
    } as FormData,
    validators: {
      onSubmit: formSchema,
    },
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value }) => {
      const parsed = formSchema.safeParse(value);
      if (!parsed.success) {
        toast({
          kind: "error",
          autoHide: false,
          title: "Error parsing form",
        });
        return;
      }

      if (warningAccounts.length > 0) {
        setPendingSubmit(parsed.data);
        return;
      }

      await runCreateMutation(parsed.data);
    },
  });

  const organisations = (data.organisations ?? []) as Array<{
    id: string;
    name: string;
    businessEntities?: Array<{ id: string; name: string }> | null;
  }>;

  const organisationOptions = organisations
    .map((org) => ({
      value: org.id,
      label: org.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedOrganisationId = useStore(
    form.store,
    (state) => state.values.organisationId,
  );
  const selectedOrganisation = organisations.find(
    (o) => o.id === selectedOrganisationId,
  );

  const businessEntityOptions = selectedOrganisation?.businessEntities
    ?.map((be) => ({
      value: be.id,
      label: be.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedKind = useStore(form.store, (state) => state.values.kind);

  const selectedBusinessEntityId = useStore(
    form.store,
    (state) => state.values.businessEntityId,
  );

  const newPaymentIdentifiers = useStore(
    form.store,
    (state) => state.values.paymentIdentifiers,
  );

  const newPaymentIdentifierCurrency = useStore(
    form.store,
    (state) => state.values.currency,
  );

  useEffect(() => {
    if (businessEntityOptions?.length === 1 && !selectedBusinessEntityId) {
      form.setFieldValue("businessEntityId", businessEntityOptions[0].value);
    }
  }, [
    businessEntityOptions?.length,
    businessEntityOptions?.[0]?.value,
    selectedBusinessEntityId,
    form,
  ]);

  useEffect(() => {
    if (!selectedOrganisationId) {
      setExistingOrgAccounts([]);
      return;
    }
    fetchOrgBeneficiaryAccounts({
      variables: { organisationId: selectedOrganisationId },
      onCompleted: (result) => {
        const accounts = result.beneficiaryAccounts?.beneficiaryAccounts ?? [];
        setExistingOrgAccounts(accounts);
      },
    });
  }, [selectedOrganisationId, fetchOrgBeneficiaryAccounts]);

  const [warningAccounts, setWarningAccounts] = useState<
    ExistingBeneficiaryAccountFragment[]
  >([]);

  useEffect(() => {
    const newWarningAccounts: ExistingBeneficiaryAccountFragment[] = [];
    for (const account of existingOrgAccounts) {
      if (account.currency !== newPaymentIdentifierCurrency) {
        continue;
      }

      for (const existingPaymentIdentifierRaw of account.paymentIdentifiers ??
        []) {
        const existingPaymentIdentifier =
          paymentIdentifierComponentToSetPaymentIdentifier(
            existingPaymentIdentifierRaw,
          );
        if (!existingPaymentIdentifier) {
          continue;
        }
        for (const newPaymentIdentifierRaw of newPaymentIdentifiers) {
          const newPaymentIdentifier = setPaymentIdentifierSchema.safeParse(
            newPaymentIdentifierRaw,
          );
          if (!newPaymentIdentifier.success) {
            continue;
          }
          if (
            paymentIdentifiersEqual(
              existingPaymentIdentifier,
              newPaymentIdentifier.data,
            ).equivalent
          ) {
            newWarningAccounts.push(account);
          }
        }
      }
    }
    setWarningAccounts(newWarningAccounts);
  }, [
    newPaymentIdentifiers,
    existingOrgAccounts,
    newPaymentIdentifierCurrency,
  ]);

  const { commonCountriesList, uncommonCountriesList } = getCountryLists();

  return (
    <AdminPageContainer testId="admin-beneficiary-new">
      <AdminPageHeader
        title="New Beneficiary Account"
        subtitle="Create a new beneficiary account"
      />

      <Dialog
        open={pendingSubmit !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingSubmit(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Duplicate {newPaymentIdentifierCurrency} payment identifiers
            </DialogTitle>
            <DialogDescription>
              The payment identifiers you entered match existing beneficiary
              accounts in this organisation:
            </DialogDescription>
          </DialogHeader>
          <ul className="flex flex-col gap-1">
            {warningAccounts.map((account) => (
              <li key={account.id}>
                <Link
                  to="/beneficiary-accounts/$beneficiaryAccountId"
                  params={{ beneficiaryAccountId: account.id }}
                  className="text-primary underline"
                  target="_blank"
                >
                  {account.displayName || account.id}
                </Link>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPendingSubmit(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              loading={loading}
              disabled={loading}
              onClick={async () => {
                if (!pendingSubmit) {
                  return;
                }
                const data = pendingSubmit;
                setPendingSubmit(null);
                await runCreateMutation(data);
              }}
            >
              Create anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.setErrorMap({
            onSubmit: {
              fields: {},
            },
          });
          form.handleSubmit();
        }}
        className="flex flex-col gap-4"
      >
        <div className="flex gap-6">
          <div className="flex flex-1 flex-col gap-4">
            <form.AppField
              name="organisationId"
              listeners={{
                onChange: () => {
                  form.resetField("businessEntityId");
                },
              }}
            >
              {(field) => (
                <field.SelectField label="Organisation" placeholder="Select...">
                  {organisationOptions.map((o) => (
                    <field.SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="kind">
              {(field) => (
                <field.SelectField label="Kind" placeholder={"Select..."}>
                  {beneficiaryAccountKindValues.map((kind) => (
                    <field.SelectItem key={kind} value={kind}>
                      {prettyPrintEnum(kind)}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            {selectedKind === "PAYEE" && (
              <form.AppField name="entityKind">
                {(field) => (
                  <field.SelectField
                    label="Entity kind"
                    placeholder={"Select..."}
                  >
                    {beneficiaryEntityKindValues.map((v) => (
                      <field.SelectItem key={v} value={v}>
                        {prettyPrintEnum(v)}
                      </field.SelectItem>
                    ))}
                  </field.SelectField>
                )}
              </form.AppField>
            )}

            {selectedKind === "VERIFIED_BENEFICIARY" && (
              <form.AppField name="businessEntityId">
                {(field) => (
                  <field.SelectField
                    label="Business entity"
                    disabled={!selectedOrganisationId}
                    placeholder={
                      !selectedOrganisationId
                        ? "Select organisation first"
                        : "Select..."
                    }
                  >
                    {businessEntityOptions?.map((be) => (
                      <field.SelectItem key={be.value} value={be.value}>
                        {be.label}
                      </field.SelectItem>
                    ))}
                  </field.SelectField>
                )}
              </form.AppField>
            )}

            <form.AppField name="accountName">
              {(field) => <field.InputField label="Account name" />}
            </form.AppField>
            <form.AppField name="nickname">
              {(field) => <field.InputField label="Nickname" />}
            </form.AppField>
            <form.AppField name="currency">
              {(field) => (
                <field.SelectField label="Currency" placeholder={"Select..."}>
                  {currencyValues.map((c) => (
                    <field.SelectItem key={c} value={c}>
                      <CurrencyLabel currencyCode={c} />
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>
            <form.AppField name="country">
              {(field) => (
                <field.SelectField label="Country" placeholder={"Select..."}>
                  {commonCountriesList.map(({ value, label }) => (
                    <SelectItem value={value} key={`common-${value}`}>
                      {label}
                    </SelectItem>
                  ))}
                  <SelectSeparator />
                  {uncommonCountriesList.map(({ value, label }) => (
                    <SelectItem value={value} key={`uncommon-${value}`}>
                      {label}
                    </SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            {selectedKind === "PAYEE" && (
              <div className="flex flex-grow flex-col gap-4">
                <Txt variant="name">Payee address</Txt>
                <div className="flex flex-col gap-4">
                  <div className="flex gap-4">
                    <form.AppField name="address.buildingNumber">
                      {(field) => <field.InputField label="Building number" />}
                    </form.AppField>
                    <form.AppField name="address.streetName">
                      {(field) => <field.InputField label="Street name" />}
                    </form.AppField>
                    <form.AppField name="address.city">
                      {(field) => <field.InputField label="City" />}
                    </form.AppField>
                  </div>

                  <div className="flex gap-4">
                    <form.AppField name="address.state">
                      {(field) => <field.InputField label="State" />}
                    </form.AppField>
                    <form.AppField name="address.postcode">
                      {(field) => <field.InputField label="Postcode" />}
                    </form.AppField>

                    <form.AppField name="address.country">
                      {(field) => (
                        <field.SelectField
                          label="Country"
                          placeholder={"Select..."}
                        >
                          {commonCountriesList.map(({ value, label }) => (
                            <SelectItem value={value} key={`common-${value}`}>
                              {label}
                            </SelectItem>
                          ))}
                          <SelectSeparator />
                          {uncommonCountriesList.map(({ value, label }) => (
                            <SelectItem value={value} key={`uncommon-${value}`}>
                              {label}
                            </SelectItem>
                          ))}
                        </field.SelectField>
                      )}
                    </form.AppField>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-1 flex-col gap-4 rounded bg-neutral-muted p-4">
            {warningAccounts.length > 0 && (
              <Alert
                kind="warning"
                title={`Duplicate ${newPaymentIdentifierCurrency} payment identifiers`}
                description={
                  <ul className="mt-1 flex flex-col gap-0.5">
                    {warningAccounts.map((account) => (
                      <li key={account.id}>
                        <Link
                          to="/beneficiary-accounts/$beneficiaryAccountId"
                          params={{ beneficiaryAccountId: account.id }}
                          className="underline"
                          target="_blank"
                        >
                          {account.displayName || account.id}
                        </Link>
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
            <EditPaymentIdentifiers
              form={form}
              fields={{ paymentIdentifiers: "paymentIdentifiers" }}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            variant="primary"
            type="submit"
            disabled={loading}
            loading={loading}
          >
            Create beneficiary
          </Button>
        </div>
      </form>
    </AdminPageContainer>
  );
}
